import { useEffect, useState } from "react";
import {
  MdMailOutline,
  MdOutlineDelete,
  MdOutlineEdit,
  MdOutlineLocalPhone,
} from "react-icons/md";
import { formatPhoneNumber } from "utils/utils";
import { ContactListType } from "utils/interfaces";

interface UserFormCardProps {
  user: ContactListType;
  selectedUsers: ContactListType[];
  primaryTag?: boolean;
  handleDelete: (user: ContactListType) => void;
  handleEdit: (user: ContactListType) => void;
}

const UserFormCard: React.FC<UserFormCardProps> = ({
  user,
  selectedUsers,
  primaryTag = true,
  handleDelete,
  handleEdit,
}) => {
  // States definition
  const [isPrimary, setIsPrimary] = useState(false);

  /**
   * Define the select status of this card
   */
  useEffect(() => {
    // Get the selected data
    const selectedContactDataIds = selectedUsers.map(
      (contactItem: ContactListType) => {
        // Check if it's created or temporal
        if (contactItem._id) return contactItem._id;
        else return contactItem.tempId;
      }
    );

    // Check if it's going to be temporal or a created one
    const userId = user._id ? user._id : user.tempId;

    // Set selected if it's included in the list
    if (selectedContactDataIds.includes(userId)) {
      // And set as primary if there is the first element in the list
      // and if the option is selected to display the primary tag
      if (primaryTag) setIsPrimary(selectedContactDataIds[0] === userId);
    } else {
      // Set if the option is selected to display the primary tag
      if (primaryTag) setIsPrimary(false);
    }
  }, [selectedUsers]);

  return (
    <div className="self-stretch text-start p-3 bg-white border border-Subtle rounded-xl justify-start items-center gap-3 inline-flex">
      <div className="grow shrink basis-0 flex-col justify-center items-start gap-2 inline-flex">
        <div className="w-[335px] h-fit justify-start items-center gap-2 inline-flex">
          <div className="overflow-hidden self-stretch break-keep whitespace-nowrap text-ellipsis text-neutral-700 text-sm font-semibold font-sans leading-[17px]">
            {user ? user.firstName + " " + user.lastName : "-"}
          </div>
          {isPrimary && (
            <div className="w-[59px] px-1.5 py-0.5 bg-accent-500 rounded justify-center items-center gap-2 flex">
              <div className="text-white text-[10px] font-normal font-sans uppercase leading-3 tracking-wide select-none">
                Primary
              </div>
            </div>
          )}
        </div>
        <div className="self-stretch justify-start items-center gap-3 inline-flex">
          <div className="self-stretch justify-between items-center gap-1 flex">
            <MdOutlineLocalPhone className="w-4 h-4 fill-neutral-500" />
            <div className="w-[74px] text-neutral-500 text-xs font-normal font-inter leading-[14px] overflow-hidden text-ellipsis text-nowrap whitespace-nowrap">
              {formatPhoneNumber(user.phone) || "-"}
            </div>
          </div>
          <div className="w-px self-stretch justify-start items-start gap-2 flex">
            <div className="w-px self-stretch bg-neutral-200" />
          </div>
          <div className="grow shrink basis-0 h-4 justify-start items-center gap-1 flex">
            <MdMailOutline className="w-4 h-4 fill-neutral-500" />
            <div className="w-[181px] text-neutral-500 text-xs font-normal font-inter leading-[14px] overflow-hidden text-ellipsis text-nowrap whitespace-nowrap">
              {user && user.email ? user.email : "-"}
            </div>
          </div>
        </div>
      </div>
      <button
        className="p-2.5 bg-neutral-50 rounded-full justify-start items-start gap-2 flex cursor-pointer hover:bg-neutral-100 ease-in-out duration-300"
        onClick={() => handleDelete(user)}
      >
        <MdOutlineDelete className="w-5 h-5 fill-neutral-700" />
      </button>
      <button
        className="p-2.5 bg-neutral-50 rounded-full justify-start items-center gap-2 flex cursor-pointer hover:bg-neutral-100 ease-in-out duration-300"
        onClick={() => handleEdit(user)}
      >
        <MdOutlineEdit className="w-5 h-5 fill-neutral-700" />
      </button>
    </div>
  );
};

export default UserFormCard;
